import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router";
import {
    ArrowLeft, Loader2, Save, Eye, CheckCircle2, AlertCircle,
} from "lucide-react";
import { ImageUpload } from "../../components/ui/image-upload";

import { blogService, type BlogPost } from "../../../lib/services/blogService";
import type { Models } from "appwrite";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import { Switch } from "../../components/ui/switch";
import { Badge } from "../../components/ui/badge";

type BlogDoc = BlogPost & Models.Document;

const CATEGORIES = ["HVAC", "Refrigeration", "Heat Pump", "Electrical", "Engineering", "Case Study"];

const EMPTY: Omit<BlogPost, "$id"> = {
    title: "",
    category: CATEGORIES[0],
    author: "",
    date: new Date().toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" }),
    readTime: "5 min read",
    status: "Published",
    excerpt: "",
    imageUrl: "",
    featured: "false",
    content: "",
    slug: "",
};

export function AdminBlogEditor() {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const isNew = !id || id === "new";

    const [form, setForm] = useState<Omit<BlogPost, "$id">>(EMPTY);
    const [loading, setLoading] = useState(!isNew);
    const [saving, setSaving] = useState(false);
    const [toast, setToast] = useState<{ type: "success" | "error"; msg: string } | null>(null);

    // Load existing post
    useEffect(() => {
        if (isNew) return;
        setLoading(true);
        blogService.list()
            .then((posts) => {
                const post = (posts as BlogDoc[]).find((p) => p.$id === id);
                if (post) {
                    setForm({
                        title: post.title,
                        category: post.category,
                        author: post.author,
                        date: post.date,
                        readTime: post.readTime,
                        status: post.status,
                        excerpt: post.excerpt ?? "",
                        imageUrl: post.imageUrl ?? "",
                        featured: post.featured ?? "false",
                        content: post.content ?? "",
                        slug: post.slug ?? "",
                    });
                }
            })
            .catch(() => showToast("error", "Failed to load post."))
            .finally(() => setLoading(false));
    }, [id, isNew]);

    const set = (k: keyof typeof form, v: string) =>
        setForm((f) => ({ ...f, [k]: v }));

    const showToast = (type: "success" | "error", msg: string) => {
        setToast({ type, msg });
        setTimeout(() => setToast(null), 3500);
    };

    const handleSave = async () => {
        if (!form.title.trim()) { showToast("error", "Title is required."); return; }
        if (!form.author.trim()) { showToast("error", "Author is required."); return; }
        if (!form.slug.trim()) { showToast("error", "URL Slug is required."); return; }
        setSaving(true);
        try {
            if (isNew) {
                await blogService.create(form);
                showToast("success", "Post published successfully.");
            } else {
                await blogService.update(id!, form);
                showToast("success", "Changes saved.");
            }
            setTimeout(() => navigate("/admin/blog"), 1200);
        } catch (e: any) {
            showToast("error", e?.message ?? "Failed to save post.");
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[60vh]">
                <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
        );
    }

    return (
        <div className="w-full min-w-0">
            {/* ── Sticky top bar ── */}
            <div className="sticky top-0 z-10 bg-background border-b border-border">
                <div className="flex items-center gap-3 px-4 sm:px-6 h-14">
                    <Button variant="ghost" size="sm" onClick={() => navigate("/admin/blog")} className="gap-1.5 text-muted-foreground hover:text-white -ml-1 shadow-none">
                        <ArrowLeft className="w-4 h-4" /> Blog
                    </Button>
                    <div className="h-4 w-px bg-border" />
                    <span className="text-sm font-medium text-foreground truncate flex-1">
                        {form.title || (isNew ? "New Post" : "Edit Post")}
                    </span>
                    <div className="flex items-center gap-2 shrink-0">
                        {form.slug && (
                            <Button variant="ghost" size="sm" asChild className="shadow-none text-muted-foreground hidden sm:flex gap-1.5">
                                <a href={`/blog/${form.slug}`} target="_blank" rel="noreferrer">
                                    <Eye className="w-3.5 h-3.5" /> Preview
                                </a>
                            </Button>
                        )}
                        <Button size="sm" onClick={handleSave} disabled={saving} className="gap-1.5 shadow-none">
                            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                            {isNew ? "Publish" : "Save"}
                        </Button>
                    </div>
                </div>
                {/* Toast */}
                {toast && (
                    <div className={`flex items-center gap-2 px-6 py-2 text-sm border-t ${toast.type === "success" ? "bg-emerald-50 text-emerald-700 border-emerald-100" : "bg-destructive/5 text-destructive border-destructive/10"}`}>
                        {toast.type === "success"
                            ? <CheckCircle2 className="w-3.5 h-3.5 shrink-0" />
                            : <AlertCircle className="w-3.5 h-3.5 shrink-0" />}
                        {toast.msg}
                    </div>
                )}
            </div>

            {/* ── Two-column body ── */}
            <div className="flex flex-col lg:flex-row gap-0 min-h-[calc(100vh-3.5rem)]">

                {/* ── LEFT: Main content ── */}
                <div className="flex-1 p-4 sm:p-6 space-y-5 border-r-0 lg:border-r border-border">

                    {/* Title */}
                    <div className="space-y-1.5">
                        <Label htmlFor="b-title" className="text-xs font-medium text-muted-foreground">Title *</Label>
                        <Input
                            id="b-title"
                            placeholder="e.g. The Complete Guide to HVAC Energy Efficiency"
                            value={form.title}
                            onChange={(e) => {
                                set("title", e.target.value);
                                if (isNew || !form.slug) {
                                    set("slug", e.target.value.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)+/g, ""));
                                }
                            }}
                            className="shadow-none text-base font-medium h-10 border-border"
                        />
                    </div>

                    {/* Excerpt */}
                    <div className="space-y-1.5">
                        <Label htmlFor="b-exc" className="text-xs font-medium text-muted-foreground">Excerpt <span className="font-normal">(shown on blog card)</span></Label>
                        <Textarea
                            id="b-exc"
                            rows={2}
                            placeholder="A short compelling summary of this post…"
                            value={form.excerpt}
                            onChange={(e) => set("excerpt", e.target.value)}
                            className="resize-none shadow-none"
                        />
                    </div>

                    {/* Divider */}
                    <div className="border-t border-border" />

                    {/* Full Body Content */}
                    <div className="space-y-1.5">
                        <div className="flex items-center justify-between">
                            <Label htmlFor="b-content" className="text-xs font-medium text-muted-foreground">Body Content *</Label>
                            <span className="text-[11px] text-muted-foreground">HTML supported: &lt;h2&gt; &lt;p&gt; &lt;ul&gt; &lt;li&gt; &lt;strong&gt;</span>
                        </div>
                        <Textarea
                            id="b-content"
                            rows={24}
                            placeholder={"<h2>Introduction</h2>\n<p>Write your article here...</p>\n\n<h2>Section Title</h2>\n<p>Continue the article...</p>"}
                            value={form.content}
                            onChange={(e) => set("content", e.target.value)}
                            className="font-mono text-sm resize-y shadow-none min-h-[400px]"
                        />
                        <p className="text-[11px] text-muted-foreground">
                            {form.content.length} characters · ~{Math.max(1, Math.round(form.content.split(/\s+/).length / 200))} min read
                        </p>
                    </div>
                </div>

                {/* ── RIGHT: Sidebar metadata ── */}
                <div className="w-full lg:w-72 xl:w-80 shrink-0 p-4 sm:p-6 space-y-5 border-t lg:border-t-0 border-border order-last lg:order-last bg-muted/20">

                    <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">Post Settings</p>

                    {/* Status + Featured */}
                    <div className="space-y-3">
                        <div className="space-y-1.5">
                            <Label className="text-xs font-medium text-muted-foreground">Status</Label>
                            <Select value={form.status} onValueChange={(v) => set("status", v)}>
                                <SelectTrigger className="shadow-none h-9"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="Published">Published</SelectItem>
                                    <SelectItem value="Draft">Draft</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>

                        <div className="flex items-center justify-between py-2 border-y border-border">
                            <div>
                                <p className="text-sm font-medium text-foreground">Featured</p>
                                <p className="text-xs text-muted-foreground">Pin to top of blog</p>
                            </div>
                            <Switch checked={form.featured === "true"} onCheckedChange={(c) => set("featured", c ? "true" : "false")} />
                        </div>
                    </div>

                    {/* Category + Author */}
                    <div className="space-y-3">
                        <div className="space-y-1.5">
                            <Label className="text-xs font-medium text-muted-foreground">Category *</Label>
                            <Select value={form.category} onValueChange={(v) => set("category", v)}>
                                <SelectTrigger className="shadow-none h-9"><SelectValue /></SelectTrigger>
                                <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                            </Select>
                        </div>

                        <div className="space-y-1.5">
                            <Label htmlFor="b-author" className="text-xs font-medium text-muted-foreground">Author *</Label>
                            <Input id="b-author" placeholder="e.g. James Carter" value={form.author}
                                onChange={(e) => set("author", e.target.value)} className="shadow-none h-9" />
                        </div>
                    </div>

                    {/* Date + Read Time */}
                    <div className="space-y-3">
                        <div className="space-y-1.5">
                            <Label htmlFor="b-date" className="text-xs font-medium text-muted-foreground">Publish Date</Label>
                            <Input id="b-date" placeholder="Mar 04, 2026" value={form.date}
                                onChange={(e) => set("date", e.target.value)} className="shadow-none h-9" />
                        </div>
                        <div className="space-y-1.5">
                            <Label htmlFor="b-rt" className="text-xs font-medium text-muted-foreground">Read Time</Label>
                            <Input id="b-rt" placeholder="5 min read" value={form.readTime}
                                onChange={(e) => set("readTime", e.target.value)} className="shadow-none h-9" />
                        </div>
                    </div>

                    {/* URL Slug */}
                    <div className="space-y-1.5">
                        <Label htmlFor="b-slug" className="text-xs font-medium text-muted-foreground">URL Slug *</Label>
                        <Input id="b-slug" placeholder="guide-to-hvac-energy" value={form.slug}
                            onChange={(e) => set("slug", e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ""))}
                            className="shadow-none h-9 font-mono text-xs" />
                        {form.slug && (
                            <p className="text-[11px] text-muted-foreground break-all">/blog/{form.slug}</p>
                        )}
                    </div>

                    {/* Cover Image */}
                    <div className="space-y-2">
                        <ImageUpload
                            label="Cover Image"
                            value={form.imageUrl}
                            onChange={(url) => set("imageUrl", url)}
                            aspectRatio="video"
                        />
                    </div>

                    {/* Status badge preview */}
                    <div className="pt-2 border-t border-border flex items-center gap-2">
                        <Badge variant="outline" className={`text-xs ${form.status === "Published" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-amber-50 text-amber-700 border-amber-200"}`}>
                            {form.status}
                        </Badge>
                        {form.featured === "true" && (
                            <Badge variant="outline" className="text-xs bg-primary/10 text-primary border-primary/20">Featured</Badge>
                        )}
                        <Badge variant="outline" className="text-xs">{form.category}</Badge>
                    </div>
                </div>
            </div>
        </div>
    );
}
